import { Dayjs } from "dayjs"
import { dayjsUTC } from "@/helpers/dateHelper"
import IParser from "@/types/IParser"
import ParseError from "@/types/ParseError"
import { IWarrantsCompletionSummary } from "@/models/admin/warrantsCompletionSummary/warrantsCompletionSummary.types"

/** Minimal group projection the summary is derived from: which account, when. */
export type WarrantsPhaseGroup = {
  accountNumber: string
  createdAt: string
}

/**
 * The inputs to the completion summary. A new communication group is created per
 * communication and tagged with the account's warrant phase (`parent_status`),
 * so a journey's duration is measured purely from group `created_at`s:
 * `warrant_phase_first` → `warrant_phase_tenth`, per account.
 */
export type WarrantsCompletionSummaryRaw = {
  /** Total communications in the window (volume context). */
  totalCommunications: number
  /** Groups at `warrant_phase_tenth` (the journey end) for the window's accounts. */
  tenthPhaseGroups: WarrantsPhaseGroup[]
  /** Groups at `warrant_phase_first` (the journey start) for those same accounts. */
  firstPhaseGroups: WarrantsPhaseGroup[]
}

/** Earliest `created_at` per account (Hasura timestamps are naive UTC). */
function earliestByAccount(groups: WarrantsPhaseGroup[]): Map<string, Dayjs> {
  const earliest = new Map<string, Dayjs>()
  groups.forEach(({ accountNumber, createdAt }) => {
    const timestamp = dayjsUTC(createdAt)
    const current = earliest.get(accountNumber)
    if (!current || timestamp.isBefore(current)) {
      earliest.set(accountNumber, timestamp)
    }
  })
  return earliest
}

export const WarrantsCompletionSummaryParser: IParser<
  IWarrantsCompletionSummary,
  WarrantsCompletionSummaryRaw
> = {
  toDomain(raw) {
    try {
      const { totalCommunications, tenthPhaseGroups, firstPhaseGroups } = raw

      const firstByAccount = earliestByAccount(firstPhaseGroups)
      const tenthByAccount = earliestByAccount(tenthPhaseGroups)

      // A completed journey is an account with both a first-phase and a
      // tenth-phase group; its duration is first → tenth (fractional days).
      const durationsInDays: number[] = []
      tenthByAccount.forEach((tenthAt, accountNumber) => {
        const firstAt = firstByAccount.get(accountNumber)
        if (!firstAt) return
        durationsInDays.push(Math.max(0, tenthAt.diff(firstAt, "day", true)))
      })

      const totalCompleted = durationsInDays.length
      const averageDaysToComplete = totalCompleted
        ? durationsInDays.reduce((sum, days) => sum + days, 0) / totalCompleted
        : null

      return {
        totalCommunications,
        totalCompleted,
        averageDaysToComplete,
      }
    } catch (error) {
      throw new ParseError(error)
    }
  },
}
